// @ts-nocheck

import { colors } from '@lmc/common/constants'
import { theme } from '@lmc/ui-kit'
import { capitalize } from 'lodash'
import type React from 'react'
import { type ChangeEvent, Component, type ReactNode } from 'react'
import ListHeaderSearch from '../../../LEGACY/ListHeaderSearch'
import { Icon, ProfilePicture } from '../../generic'

type Filter = {
  value: string
  badgeText?: string
  badgeColor?: string
  count?: number
  icon?: string
  iconColor?: string
  label?: string
  picture?: string
}

type PropertyFilterListProps = {
  active: string[] | undefined
  filterKey: string | undefined
  filters: Filter[] | undefined
  onChange: (filter: { key: string | undefined; value: string }) => void
  showSearch: boolean
  style: React.CSSProperties
  showClear: boolean
  singleSelect: boolean
  testId?: string
}

type PropertyFilterListState = {
  search: string
}

export default class PropertyFilterList extends Component<
  PropertyFilterListProps,
  PropertyFilterListState
> {
  constructor(props: PropertyFilterListProps) {
    super(props)
    this.state = {
      search: ''
    }
  }

  clearAll = () => {
    this.handleChange([])
  }

  handleChange = (value: string[]) => {
    this.props.onChange({
      key: this.props.filterKey,
      value
    })
  }

  handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({ search: e.target.value })
  }

  handleSearchClear = () => {
    this.setState({ search: '' })
  }

  getSearchResults = () => {
    const { filters, showSearch } = this.props
    if (!showSearch) return filters

    const searchRegexp = new RegExp(
      this.state.search.replace(/([.?*+^$[\]\\(){}|-])/g, '\\$1'),
      'i'
    )
    return filters?.filter((filter) => {
      const displayLabel = filter.label || capitalize(filter.value)
      return searchRegexp.test(displayLabel)
    })
  }

  renderIcon = (filter: Filter) => {
    if (filter.picture) {
      return (
        <ProfilePicture
          url={filter.picture}
          size={28}
          style={styles.profilePicture}
        />
      )
    }
    if (filter.icon || filter.iconColor) {
      return (
        <div
          style={{
            ...styles.iconContainer,
            background: filter.iconColor
          }}
        >
          <img alt="" src={filter.icon} style={styles.icon} />
        </div>
      )
    }
    return null
  }

  renderFilter = (filter: Filter, index: number) => {
    const {
      badgeText,
      badgeColor,
      count,
      icon,
      iconColor,
      label,
      picture,
      value
    } = filter

    const isActive = this.props.active && this.props.active.includes(value)
    const displayLabel = label || capitalize(value)
    const filterTestId = this.props.testId
      ? `${this.props.testId}-filter-item-${index}`
      : undefined

    return (
      <div
        key={value}
        onClick={() => this.handleChange(value)}
        style={{
          ...styles.filter,
          ...(this.props.singleSelect ? styles.singleSelect : {}),
          ...(this.props.singleSelect && isActive
            ? styles.singleSelectActive
            : {})
        }}
        data-testid={filterTestId}
      >
        <div style={styles.filterContent}>
          {this.renderIcon(filter)}
          <span style={styles.label}>
            {typeof displayLabel === 'string' ? displayLabel : 'N/A'}
          </span>
          {(count || count === 0) && (
            <span style={styles.count}>({count})</span>
          )}
          {badgeText && (
            <span
              style={{
                ...styles.badge,
                background: badgeColor
              }}
            >
              {badgeText}
            </span>
          )}
        </div>
        {!this.props.singleSelect && (
          <Icon
            name={isActive ? 'checkbox-on' : 'checkbox-off'}
            fill={isActive ? theme.color.status.primary : undefined}
            size={24}
            style={styles.checkbox}
          />
        )}
      </div>
    )
  }

  render(): ReactNode {
    const {
      filters,
      showSearch,
      style,
      showClear = true,
      singleSelect = false
    } = this.props
    const { search } = this.state
    if (!filters) return null
    const searchResults = this.getSearchResults()

    return (
      <div
        style={{
          ...style,
          ...styles.wrapper,
          ...(singleSelect ? styles.singleSelectWrapper : {})
        }}
      >
        {showSearch && (
          <ListHeaderSearch
            handleChange={this.handleSearchChange}
            handleClear={this.handleSearchClear}
            inputStyle={styles.searchInput}
            value={search}
            testId={
              this.props.testId ? `${this.props.testId}-search` : undefined
            }
          />
        )}
        <div style={styles.list}>
          {searchResults?.map((filter, index) =>
            this.renderFilter(filter, index)
          )}
        </div>
        {showClear && (
          <>
            <div style={styles.divider} />
            <a
              onClick={this.clearAll}
              style={styles.link}
              data-testid={
                this.props.testId ? `${this.props.testId}-clear-all` : undefined
              }
            >
              Clear
            </a>
          </>
        )}
      </div>
    )
  }
}

const styles = {
  badge: {
    color: colors.black,
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 5,
    padding: '0px 4px',
    borderRadius: 15
  },
  checkbox: {
    flexShrink: 0
  },
  count: {
    color: colors.darkgrey,
    display: 'inline-block',
    marginLeft: 5
  },
  divider: {
    background: colors.grey,
    height: 2,
    marginBottom: 10
  },
  filter: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: 3,
    userSelect: 'none'
  },
  filterContent: {
    alignItems: 'center',
    display: 'flex'
  },
  icon: {
    width: 12
  },
  iconContainer: {
    alignItems: 'center',
    borderRadius: 28,
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    height: 28,
    marginRight: 10,
    width: 28
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  link: {
    color: colors.black,
    textDecoration: 'underline'
  },
  list: {
    maxHeight: 200,
    overflow: 'auto',
    padding: '10px 0px'
  },
  profilePicture: {
    flexShrink: 0,
    marginRight: 10
  },
  searchInput: {
    height: 30
  },
  topContainer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  wrapper: {
    background: 'white',
    border: '1px solid',
    borderColor: colors.grey,
    borderRadius: 4,
    padding: '10px 12px'
  },
  singleSelectWrapper: {
    padding: 0,
    width: '214px'
  },
  singleSelect: {
    padding: '4px 12px'
  },
  singleSelectActive: {
    background: '#E6F7FF'
  }
}
